import * as React from 'react';
// src/contexts/RegistrationContext.tsx

import { createContext, useContext } from 'react';
import type PLAN_INTERVALS from '../utils/constants/contractor-plan-intervals';
import type CONTRACTOR_PLANS from '../utils/constants/data/contractor-plan-options';
import type { CustomerTypeValue } from '../utils/constants/data/customer-types';
import type { PromoCode } from '../types/promoCode';
import { UserAddress } from '@/types/user';

interface Trade {
  full_specialty_name: string;
  industry: 'Residential' | 'Commercial';
  _id?: string;
  division?: string;
  specialty_id?: string;
}

interface User {
  user_id?: string;
  plan_interval?: PLAN_INTERVALS;
  plan?: CONTRACTOR_PLANS;
  contact_email?: string;
  contact_name?: string;
  user_type?: CustomerTypeValue | 'contractor';
  company_name?: string;
  contact_phone?: string;
  address_one?: string;
  address_two?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  website?: string;
  years_of_experience?: number;
  years_in_business?: number;
  workmanship_warranty?: boolean;
  is_insured?: boolean;
  is_licensed?: boolean;
  has_free_estimates?: boolean;
  has_financing?: boolean;
  is_manufacturer_certified?: boolean;
  is_locally_owned?: boolean;
  is_veteran_owned?: boolean;

  communication?: {
    contact_email?: string;
    contact_phone?: string;
  };

  password?: string;
  confirm_password?: string;
  personal_city?: string;
  personal_state?: string;
  personal_zip_code?: string;

  error_message?: string;
  is_card_valid?: boolean;
  setup_intent?: string;

  address?: UserAddress;
}

interface RegistrationContextType {
  resetRegistrationContext: () => void;
  hasPreviouslyMounted: boolean;
  user: User;
  resetUser: () => void;
  updateUserField: <K extends keyof User>(field: K, value: User[K]) => void;
  updateUser: (updatedUserFields: Partial<User>) => void;
  promoCodes: PromoCode[];
  addPromoCode: (promoCode: PromoCode) => void;
  removePromoCode: (promoCodeToBeRemoved: PromoCode) => void;
  clearPromoCodes: () => void;
  trades: Trade[];
  setTrades: React.Dispatch<React.SetStateAction<Trade[]>>;
  resetTrades: () => void;
  addTrade: (trade: Trade) => void;
  addTrades: (trades: Trade[]) => void;
  removeTrade: (tradeToBeRemoved: Trade) => void;
}

// The context and the custom hook are exported from here
export const RegistrationContext = createContext<
  RegistrationContextType | undefined
>(undefined);

export const useRegistrationContext = () => {
  const context = useContext(RegistrationContext);

  if (!context) {
    throw new Error(
      'useRegistrationContext must be used within a RegistrationProvider'
    );
  }

  return context;
};

// You can also export the types from here if other files need them
export type { Trade, PromoCode, User };
